import { useEffect, useState } from "react";
import { AsyncPaginate } from "react-select-async-paginate";
import { useLocation, useNavigate } from "react-router-dom";
import { redirectToLogin } from "../utils/auth";
import { Loading } from "../Components/Loading/Loading";
import { useUser } from "../context/UserContext";
import { Comments } from "../Components/Loa/Comments";
import { History } from "../Components/Loa/History";
import { LOA_STATUS_LABELS, getLoaStatusLabel } from "../constants/loaStatus";

const tabs = [
  { heading: "Request Details", id: "requestDetails" },
  { heading: "Discussion", id: "discussion" },
  { heading: "History", id: "history" },
];

const tileCard = "bg-white rounded-xl p-6 shadow-[0_2px_8px_rgba(0,0,0,0.05)] mb-5";

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "44px",
    borderRadius: "8px",
    fontSize: "12px",
    borderColor: state.isFocused ? "#3e0084" : "#d1d5db",
    boxShadow: state.isFocused ? "0 0 0 1px #3e0084" : "none",
    "&:hover": { borderColor: "#3e0084" },
  }),
  multiValue: (base) => ({ ...base, backgroundColor: "#f4f0fa", borderRadius: "15px", border: "1px solid #dcd0f0", paddingLeft: "4px" }),
  multiValueLabel: (base) => ({ ...base, color: "#3e0084", fontWeight: "bold", fontSize: "12px" }),
  multiValueRemove: (base) => ({ ...base, color: "#3e0084", ":hover": { backgroundColor: "#dcd0f0", color: "#3e0084" } }),
};

export const LoaDetail = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loa, setLoa] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [updatingState, setUpdatingState] = useState(false);
  const [stateMenuOpen, setStateMenuOpen] = useState(false);

  // Inline edit fields (like the Ticket page — editable in place, saved via Update).
  const [title, setTitle] = useState("");
  const [assistants, setAssistants] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  const authToken = localStorage.getItem("authToken");
  const navigate = useNavigate();
  const location = useLocation();

  const { user } = useUser();
  const canChangeState = user.canApproveLoa === true;
  // Editable only while Submitted (1) or Rejected (4), by the creator or an approver.
  const canEdit =
    loa &&
    (loa.status === "1" || loa.status === "4") &&
    (loa.createdBy === user.username || user.canApproveLoa === true);

  useEffect(() => {
    if (!authToken) {
      redirectToLogin();
      return;
    }
    if (!user.loaded) return;

    const BASE = process.env.REACT_APP_BACKEND_URL;
    const loaId = location.pathname.split("/").pop();
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`,
    };
    const get = (url) =>
      fetch(url, { method: "GET", headers }).then((r) => {
        if (r.status === 401) {
          redirectToLogin();
          return null;
        }
        return r.json();
      });

    Promise.all([
      get(`${BASE}/API/V2/LOA/${loaId}`),
      get(`${BASE}/API/V2/LOA/${loaId}/media`),
    ])
      .then(([loaData, filesData]) => {
        if (!loaData) return;
        if (loaData.error) {
          alert(loaData.error);
          return;
        }
        setLoa(loaData);
        setTitle(loaData.title || "");
        setAssistants((loaData.assistants || []).map((a) => ({ value: a, label: a })));
        setUploadedFiles(filesData || []);
      })
      .catch((err) => console.error("LOA load error:", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.loaded]);

  const handleDownload = async (fileName) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/API/V2/LOA/${loa.loaId}/media/${fileName}`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${authToken}` },
        },
      );
      if (!response.ok) throw new Error("Failed to download file");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  const deleteUploaded = async (fileName) => {
    if (!window.confirm(`Delete "${fileName}"? This cannot be undone.`)) return;
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/API/V2/LOA/${loa.loaId}/media/${encodeURIComponent(fileName)}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${authToken}` },
        },
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        alert(data.error || "Failed to delete file");
        return;
      }
      setUploadedFiles((prev) => prev.filter((f) => f !== fileName));
    } catch (error) {
      console.error("Error deleting file:", error);
      alert("An error occurred while deleting the file.");
    }
  };

  // Assistants (bots) come straight from JBM via the backend proxy, same as the create form.
  const loadAssistantOptions = async (searchQuery, loadedOptions, { page }) => {
    const limit = 50; // JBM caps a page at 50
    const offset = (page - 1) * limit;

    const res = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/API/V2/getAllBots?offset=${offset}&limit=${limit}&search=${encodeURIComponent(searchQuery)}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      },
    );
    if (res.status === 401) { redirectToLogin(); return { options: [], hasMore: false }; }
    if (!res.ok) { return { options: [], hasMore: false }; }

    const data = await res.json();
    // Show both the assistant name and its stable botId; store the combined
    // string so both survive a reload (the LOA `assistants` field is [String]).
    const options = (data.bots || []).map((b) => {
      const label = `${b.displayName} (${b.botId})`;
      return { label, value: label };
    });
    const hasMore = offset + limit < (data.totalCount || 0);
    return { options, hasMore, additional: { page: page + 1 } };
  };

  const changeState = async (newStatus) => {
    setStateMenuOpen(false);
    if (!loa || newStatus === loa.status) return;
    setUpdatingState(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/API/V2/LOA/${loa.loaId}/state`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify({ status: newStatus }),
        },
      );
      const data = await response.json();
      if (!response.ok) {
        alert(data.error || "Failed to update state");
        return;
      }
      setLoa(data.loa);
    } catch (error) {
      console.error("Error updating LOA state:", error);
      alert("An error occurred while updating the state.");
    } finally {
      setUpdatingState(false);
    }
  };

  const saveEdits = async () => {
    if (!title || assistants.length === 0) {
      alert("Title and at least one assistant are required.");
      return;
    }
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("title", title);
      formData.append("assistants", JSON.stringify(assistants.map((a) => a.value)));
      newFiles.forEach((file) => formData.append("files", file));

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/API/V2/LOA/${loa.loaId}`,
        {
          method: "PUT",
          headers: { Authorization: `Bearer ${authToken}` },
          body: formData,
        },
      );
      const data = await response.json();
      if (!response.ok) {
        alert(data.error || data.message || "Failed to update LOA");
        return;
      }

      setLoa(data.loa);
      setTitle(data.loa.title || "");
      setAssistants((data.loa.assistants || []).map((a) => ({ value: a, label: a })));

      if (newFiles.length) {
        const filesRes = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/API/V2/LOA/${loa.loaId}/media`,
          { headers: { Authorization: `Bearer ${authToken}` } },
        );
        if (filesRes.ok) setUploadedFiles(await filesRes.json());
      }
      setNewFiles([]);
      alert("LOA updated successfully!");
    } catch (error) {
      console.error("Error updating LOA:", error);
      alert("An error occurred while updating the LOA.");
    } finally {
      setSaving(false);
    }
  };

  if (!user.loaded || !loa) {
    return <Loading />;
  }

  const activeTabId = tabs[activeTab]?.id;

  const stateColor = (key) =>
    key === "3" ? "text-green-600" : key === "4" ? "text-jiored1" : "text-gray-800";

  return (
    <div className="flex flex-col p-6 min-h-[calc(100vh-72px)]">
      <div className="flex justify-between items-center mb-6">
        <div>
          <button
            className="text-xs text-jiojbm50 font-bold"
            onClick={() => navigate("/LOA")}
          >
            ← Back to Listing
          </button>
          <div className="flex items-center gap-3 mt-1">
            <h2 className="text-lg font-bold">Action Ticket: {loa.loaId}</h2>
            {canEdit && (
              <button
                type="button"
                disabled={saving}
                onClick={saveEdits}
                className={`text-xs px-5 py-1.5 rounded-full font-bold text-white ${
                  saving ? "opacity-50 cursor-not-allowed bg-gray-400" : "bg-jiojbmpurple hover:opacity-90"
                }`}
              >
                {saving ? "Saving..." : "Update"}
              </button>
            )}
          </div>
        </div>

        {canChangeState && (
          <div className="relative w-[200px]">
            <div className="text-xs font-semibold text-gray-800 mb-2">
              Current State: {getLoaStatusLabel(loa.status)}
            </div>
            <button
              type="button"
              disabled={updatingState}
              onClick={() => setStateMenuOpen((o) => !o)}
              className="w-full border border-gray-200 rounded-lg bg-white p-2 px-3 text-sm text-left"
            >
              Change State ▼
            </button>
            {stateMenuOpen && (
              <div className="absolute top-full left-0 w-full bg-white border border-gray-200 rounded-lg mt-1 shadow-[0_4px_12px_rgba(0,0,0,0.1)] z-10">
                {Object.entries(LOA_STATUS_LABELS).map(([key, label]) => (
                  <div
                    key={key}
                    onClick={() => changeState(key)}
                    className={`p-2.5 px-4 text-sm cursor-pointer hover:bg-jiojbm20 ${stateColor(key)} ${
                      key === loa.status ? "font-bold" : ""
                    }`}
                  >
                    {label}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Tab Bar */}
      <div className="flex gap-8 border-b border-gray-200 pb-2.5 mb-5">
        {tabs.map((tab, index) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(index)}
            className={`text-sm font-bold pb-1.5 ${
              activeTab === index ? "border-b-[3px] border-jiojbm50" : ""
            }`}
          >
            {tab.heading}
          </button>
        ))}
      </div>

      {/* Tab: Request Details */}
      {activeTabId === "requestDetails" && (
        <div>
          <div className={tileCard}>
            <p className="text-xs font-semibold text-gray-800 mb-2">Request Title</p>
            <p className="text-base font-bold">{loa.title}</p>
            <p className="text-xs text-gray-500 mt-2">Organization: {loa.org}</p>
          </div>

          <div className={tileCard}>
            <p className="text-xs font-semibold text-gray-800 mb-2">Mapped Assistants</p>
            {canEdit ? (
              <AsyncPaginate
                isMulti
                value={assistants}
                loadOptions={loadAssistantOptions}
                onChange={(selected) => setAssistants(selected || [])}
                additional={{ page: 1 }}
                placeholder="Select Assistants to Map..."
                isSearchable
                debounceTimeout={300}
                styles={selectStyles}
                loadingMessage={() => "Loading Assistants, please wait..."}
              />
            ) : (
              <div className="flex flex-wrap gap-2">
                {(loa.assistants || []).map((a) => (
                  <span
                    key={a}
                    className="bg-jiojbm20 text-jiojbm50 px-3 py-1 rounded-full text-xs font-bold border border-jiojbm30"
                  >
                    {a}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className={tileCard}>
            <p className="text-xs font-semibold text-gray-800 mb-2">
              Uploaded Document{uploadedFiles.length !== 1 ? "s" : ""}
            </p>
            {uploadedFiles.length === 0 && (
              <p className="text-xs text-gray-500">No documents uploaded.</p>
            )}
            <div className="flex flex-col gap-2">
              {uploadedFiles.map((fileName) => (
                <div
                  key={fileName}
                  className="flex items-center gap-2.5 p-3.5 border border-gray-200 rounded-lg bg-gray-50"
                >
                  <span className="text-2xl">📄</span>
                  <div className="flex-1">
                    <div className="font-bold text-sm">{fileName}</div>
                    <button
                      type="button"
                      className="text-jiojbm50 text-xs"
                      onClick={() => handleDownload(fileName)}
                    >
                      Download File
                    </button>
                  </div>
                  {canEdit && (
                    <button
                      type="button"
                      title="Delete document"
                      className="text-jiored1 hover:text-red-700 text-sm font-bold px-2"
                      onClick={() => deleteUploaded(fileName)}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>

            {canEdit && (
              <div className="mt-4">
                <input
                  type="file"
                  multiple
                  accept=".xls,.xlsx,.pdf,.jpg,.jpeg,.png,.gif,.doc,.docx"
                  onChange={(e) =>
                    setNewFiles((prev) => [...prev, ...Array.from(e.target.files)])
                  }
                  className="w-full border border-gray-300 rounded-lg text-sm p-2.5 focus:outline-none focus:border-jiojbmpurple"
                />
                {newFiles.length > 0 && (
                  <div className="mt-2 flex flex-col gap-1">
                    {newFiles.map((f, i) => (
                      <div
                        key={i}
                        className="flex justify-between items-center border border-gray-200 rounded-lg p-2 px-3 text-xs"
                      >
                        <span>{f.name}</span>
                        <button
                          type="button"
                          className="text-jiored1 font-bold"
                          onClick={() => setNewFiles((prev) => prev.filter((_, idx) => idx !== i))}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-[10px] text-gray-400 mt-1">
                  New files are attached when you press Update.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tab: Discussion */}
      {activeTabId === "discussion" && (
        <div className="flex-1 min-h-[20rem]">
          <Comments loaId={loa.loaId} org={loa.org} authToken={authToken} />
        </div>
      )}

      {/* Tab: History */}
      {activeTabId === "history" && (
        <div className="flex-1 min-h-[20rem]">
          <History loaId={loa.loaId} authToken={authToken} />
        </div>
      )}
    </div>
  );
};
